import { CheckCircle2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { AuthCard } from '@/components/AuthCard';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/field';
import { requestPasswordReset } from '@/lib/auth-client';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setSubmitting(true);

    const result = await requestPasswordReset({
      email: email.trim(),
      redirectTo: '/reset-password',
    });

    setSubmitting(false);

    if (result.error) {
      setError('That could not be sent. Try again, or contact the office.');
      return;
    }
    setSent(true);
  }

  if (sent) {
    return (
      <AuthCard
        title="Check your email"
        footer={
          <Link className="text-body text-bark-700 underline underline-offset-4" to="/login">
            Back to sign in
          </Link>
        }
      >
        <div className="space-y-4">
          <p className="flex items-start gap-2 text-body text-ink-700">
            <CheckCircle2
              aria-hidden="true"
              className="mt-0.5 size-[18px] shrink-0 text-status-completed-text"
            />
            {/*
             * Deliberately neutral about whether the address exists. Saying
             * "no account with that email" turns this form into a way to find
             * out who has one.
             */}
            <span>
              If that address is on an account, a link to set a new password is on its way. It
              expires in an hour.
            </span>
          </p>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Reset your password"
      description="We will email you a link to set a new one."
      footer={
        <Link className="text-body text-bark-700 underline underline-offset-4" to="/login">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-5" noValidate>
        <FormError>{error}</FormError>

        <Field
          label="Email address"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          required
          placeholder="you@example.com"
          // The account of someone who signs in with a username has a recovery
          // address on it, and that is what to use here. Without this the
          // employee accounts look like they have no way back in.
          hint="If you sign in with a username, use the recovery email on your account."
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />

        <Button type="submit" full size="lg" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send the link'}
        </Button>
      </form>
    </AuthCard>
  );
}
